<template>
  <div data-role="status-shell">
    <div class="app-toast-stack" data-role="toast-stack" aria-live="polite" aria-atomic="true">
      <div
        v-for="toast in visibleToasts"
        :key="toast.id"
        class="alert app-toast"
        :class="[`alert-${toast.level}`, { 'app-progress-toast': !!toast.progress, 'is-hiding': !!toast.hiding }]"
      >
        {{ toast.text }}
      </div>
    </div>

    <div class="messages" data-role="progress-message" style="display:none">
      <div class="alert alert-info">
        <span></span>
      </div>
    </div>

    <div data-role="progress-bar-shell" role="progressbar" class="progress progress-striped active" style="display:none">
      <div class="progress-bar" data-role="progress-bar" style="width: 0%;"></div>
    </div>

    <div
      data-role="clone-progress-overlay"
      class="clone-progress-overlay"
      :style="{ display: overlay.visible ? 'block' : 'none' }"
    >
      <div class="clone-progress-panel">
        <div class="clone-progress-ring">
          <svg viewBox="0 0 120 120" class="clone-progress-svg" aria-hidden="true">
            <circle class="clone-progress-track" cx="60" cy="60" r="52"></circle>
            <circle
              class="clone-progress-value"
              data-role="clone-progress-value"
              cx="60"
              cy="60"
              r="52"
              :style="overlayStrokeStyle"
            ></circle>
          </svg>
          <div class="clone-progress-number" data-role="clone-progress-number">
            {{ overlay.percent }}%
          </div>
        </div>

        <div
          data-role="clone-progress-migration-visual"
          class="clone-progress-migration-visual"
          :style="{ display: overlay.showMigrationVisual ? 'flex' : 'none' }"
        >
          <div class="clone-progress-server-card">
            <div class="clone-progress-server-label">FROM</div>
            <div class="clone-progress-server-name" data-role="clone-progress-from-hv">
              {{ overlay.fromHv }}
            </div>
          </div>
          <div class="clone-progress-migration-lane">
            <div class="clone-progress-migration-line"></div>
            <div
              class="clone-progress-migration-packet"
              data-role="clone-progress-migration-packet"
              :style="{ left: `${overlay.percent}%` }"
            ></div>
          </div>
          <div class="clone-progress-server-card">
            <div class="clone-progress-server-label">TO</div>
            <div class="clone-progress-server-name" data-role="clone-progress-to-hv">
              {{ overlay.toHv }}
            </div>
          </div>
        </div>

        <h4 data-role="clone-progress-title">{{ overlay.title }}</h4>
        <p data-role="clone-progress-text">{{ overlay.text }}</p>
        <p data-role="clone-progress-bytes" class="text-muted">{{ overlay.bytes }}</p>
        <div
          data-role="clone-progress-error"
          class="alert alert-danger"
          :style="{ display: overlay.error ? 'block' : 'none' }"
        >
          {{ overlay.error }}
        </div>
        <div class="clone-progress-actions">
          <button
            type="button"
            data-role="clone-progress-close"
            class="btn btn-default"
            :style="{ display: overlay.closeVisible ? 'inline-block' : 'none' }"
            @click="hideCloneOverlay"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, reactive, ref } from "vue"

const props = defineProps({
  flashMessages: {
    type: Array,
    default: () => []
  }
})

const CLONE_PROGRESS_CIRCUMFERENCE = 2 * Math.PI * 52
const SCC_PROGRESS_STROKE_DEFAULT = "#2a7f62"
const SCC_PROGRESS_STROKE_DANGER = "#c13b32"
const SCC_PROGRESS_STROKE_MIGRATION = "#2563eb"

let toastCounter = 0
let sccProgressTimer = null
let cloneProgressTimer = null
let initialized = false

const visibleToasts = ref([])
const toastTimers = new Map()
const overlay = reactive({
  visible: false,
  percent: 0,
  stroke: SCC_PROGRESS_STROKE_DEFAULT,
  title: "Preparing clone...",
  text: "Please wait while the VM image is copied.",
  bytes: "",
  fromHv: "Source",
  toHv: "Destination",
  showMigrationVisual: false,
  error: "",
  closeVisible: false
})

const overlayStrokeStyle = computed(() => {
  const safePercent = Math.max(0, Math.min(100, parseInt(overlay.percent || 0, 10)))
  const offset = CLONE_PROGRESS_CIRCUMFERENCE - ((safePercent / 100) * CLONE_PROGRESS_CIRCUMFERENCE)

  return {
    stroke: overlay.stroke,
    strokeDashoffset: offset
  }
})

function clearToastTimer(id) {
  const timer = toastTimers.get(id)
  if (timer) {
    window.clearTimeout(timer)
    toastTimers.delete(id)
  }
}

function removeToast(id) {
  clearToastTimer(id)
  const target = visibleToasts.value.find((toast) => toast.id === id)
  if (!target) {
    return
  }
  target.hiding = true
  window.setTimeout(() => {
    visibleToasts.value = visibleToasts.value.filter((toast) => toast.id !== id)
  }, 180)
}

function scheduleToastRemoval(id, duration) {
  clearToastTimer(id)
  toastTimers.set(
    id,
    window.setTimeout(() => {
      removeToast(id)
    }, duration)
  )
}

function addToast(detail = {}) {
  const toast = {
    id: detail.id || `toast-${toastCounter++}`,
    level: detail.level || "info",
    text: detail.text || "",
    progress: !!detail.progress,
    hiding: false
  }

  if (!toast.text) {
    return
  }

  visibleToasts.value.push(toast)
  if (detail.duration !== 0) {
    scheduleToastRemoval(toast.id, detail.duration || 4200)
  }
}

function removeProgressToasts() {
  visibleToasts.value
    .filter((toast) => toast.progress)
    .forEach((toast) => removeToast(toast.id))
}

function resetOverlayState() {
  overlay.visible = false
  overlay.percent = 0
  overlay.stroke = SCC_PROGRESS_STROKE_DEFAULT
  overlay.title = "Preparing clone..."
  overlay.text = "Please wait while the VM image is copied."
  overlay.bytes = ""
  overlay.fromHv = "Source"
  overlay.toHv = "Destination"
  overlay.showMigrationVisual = false
  overlay.error = ""
  overlay.closeVisible = false
}

function patchStatusOverlay(detail = {}) {
  if (detail.action === "reset") {
    resetOverlayState()
    return
  }

  if (detail.action === "hide") {
    overlay.visible = false
    overlay.closeVisible = false
    overlay.error = ""
    return
  }

  if (Object.prototype.hasOwnProperty.call(detail, "visible")) overlay.visible = !!detail.visible
  if (Object.prototype.hasOwnProperty.call(detail, "percent")) overlay.percent = Math.max(0, Math.min(100, parseInt(detail.percent || 0, 10)))
  if (Object.prototype.hasOwnProperty.call(detail, "stroke")) overlay.stroke = detail.stroke || SCC_PROGRESS_STROKE_DEFAULT
  if (Object.prototype.hasOwnProperty.call(detail, "title")) overlay.title = detail.title || ""
  if (Object.prototype.hasOwnProperty.call(detail, "text")) overlay.text = detail.text || ""
  if (Object.prototype.hasOwnProperty.call(detail, "bytes")) overlay.bytes = detail.bytes || ""
  if (Object.prototype.hasOwnProperty.call(detail, "fromHv")) overlay.fromHv = detail.fromHv || "Source"
  if (Object.prototype.hasOwnProperty.call(detail, "toHv")) overlay.toHv = detail.toHv || "Destination"
  if (Object.prototype.hasOwnProperty.call(detail, "showMigrationVisual")) overlay.showMigrationVisual = !!detail.showMigrationVisual
  if (Object.prototype.hasOwnProperty.call(detail, "error")) overlay.error = detail.error || ""
  if (Object.prototype.hasOwnProperty.call(detail, "closeVisible")) overlay.closeVisible = !!detail.closeVisible
}

function sccStorePendingToast(level, message) {
  if (!message || !window.sessionStorage) {
    return
  }

  try {
    window.sessionStorage.setItem("scc:pending-toast", JSON.stringify({
      level: level || "success",
      message
    }))
  } catch (_error) {
  }
}

function sccReplayPendingToast() {
  if (!window.sessionStorage) {
    return
  }

  let raw = null
  let payload = null

  try {
    raw = window.sessionStorage.getItem("scc:pending-toast")
    if (!raw) {
      return
    }
    payload = JSON.parse(raw)
    window.sessionStorage.removeItem("scc:pending-toast")
  } catch (_error) {
    try {
      window.sessionStorage.removeItem("scc:pending-toast")
    } catch (__error) {
    }
    return
  }

  if (payload && payload.message) {
    sccShowToast(payload.level || "success", payload.message)
  }
}

function clearSccOverlayPolling() {
  if (cloneProgressTimer) {
    clearInterval(cloneProgressTimer)
    cloneProgressTimer = null
  }
}

function setSccOverlayStroke(color) {
  patchStatusOverlay({
    stroke: color || SCC_PROGRESS_STROKE_DEFAULT
  })
}

function setSccOverlayTexts(options = {}) {
  const detail = {}

  if (Object.prototype.hasOwnProperty.call(options, "title")) detail.title = options.title || ""
  if (Object.prototype.hasOwnProperty.call(options, "text")) detail.text = options.text || ""
  if (Object.prototype.hasOwnProperty.call(options, "bytes")) detail.bytes = options.bytes || ""
  if (Object.prototype.hasOwnProperty.call(options, "fromHv")) detail.fromHv = options.fromHv || "Source"
  if (Object.prototype.hasOwnProperty.call(options, "toHv")) detail.toHv = options.toHv || "Destination"

  if (Object.keys(detail).length) {
    patchStatusOverlay(detail)
  }
}

function showSccProgressOverlay(options = {}) {
  clearSccOverlayPolling()
  patchStatusOverlay({
    visible: true,
    percent: options.percent || 0,
    stroke: options.stroke || SCC_PROGRESS_STROKE_DEFAULT,
    title: options.title || "",
    text: options.text || "",
    bytes: options.bytes || "",
    fromHv: options.fromHv || "Source",
    toHv: options.toHv || "Destination",
    showMigrationVisual: !!options.showMigrationVisual,
    error: "",
    closeVisible: false
  })
}

function markCloneProgressFailed(message) {
  clearSccOverlayPolling()
  patchStatusOverlay({
    error: message,
    stroke: SCC_PROGRESS_STROKE_DANGER,
    closeVisible: true
  })
}

function sccSetCloneProgress(percent) {
  patchStatusOverlay({
    percent: Math.max(0, Math.min(100, parseInt(percent || 0, 10)))
  })
}

function hideCloneOverlay() {
  clearSccOverlayPolling()
  patchStatusOverlay({
    action: "hide"
  })
}

function completeSccProgressOverlay(redirectUrl, fallbackUrl, delay, successMessage) {
  clearSccOverlayPolling()
  sccSetCloneProgress(100)

  if (successMessage) {
    sccStorePendingToast("success", successMessage)
  }

  window.setTimeout(() => {
    window.location.href = redirectUrl || fallbackUrl
  }, delay || 900)
}

function completeSccMigrationOverlay(message) {
  clearSccOverlayPolling()
  sccSetCloneProgress(100)
  setSccOverlayTexts({
    text: message || "Migration completed."
  })
  sccShowToast("success", message || "Migration completed.")

  window.setTimeout(() => {
    hideCloneOverlay()
  }, 900)
}

function startSccProgress(message) {
  if (sccProgressTimer) {
    clearInterval(sccProgressTimer)
    sccProgressTimer = null
  }

  sccDismissProgressToast()
  addToast({
    id: "progress-toast",
    level: "info",
    text: message,
    progress: true,
    duration: 5200
  })

  sccProgressTimer = window.setTimeout(() => {
    removeToast("progress-toast")
    sccProgressTimer = null
  }, 5200)

  return true
}

function sccDismissProgressToast() {
  if (sccProgressTimer) {
    clearTimeout(sccProgressTimer)
    sccProgressTimer = null
  }

  removeProgressToasts()
}

function sccShowToast(level, message) {
  if (!message) {
    return
  }
  addToast({
    level: level || "info",
    text: message,
    duration: 4200
  })
}

function sccRequestJson(url, options = {}) {
  const method = (options.method || "GET").toUpperCase()
  const headers = Object.assign({
    Accept: "application/json",
    "X-Requested-With": "XMLHttpRequest"
  }, options.headers || {})
  const fetchOptions = {
    method,
    headers,
    credentials: "same-origin"
  }

  if (options.body) {
    fetchOptions.body = options.body
  }

  if (method !== "GET" && method !== "HEAD") {
    let token = document.querySelector("meta[name='csrf-token']")
    token = token ? token.getAttribute("content") : ""
    if (token) {
      headers["X-CSRF-Token"] = token
    }
  }

  return fetch(url, fetchOptions).then((response) => response.text().then((text) => {
    let payload = null
    if (text) {
      try {
        payload = JSON.parse(text)
      } catch (_error) {
        payload = null
      }
    }

    if (!response.ok) {
      throw new Error((payload && payload.message) || text || options.fallbackError || "Request failed.")
    }
    return payload
  }))
}

function sccShowMigrationOverlay(vmName, fromHv, toHv) {
  showSccProgressOverlay({
    title: `Migrating ${vmName}`,
    text: "Preparing migration...",
    bytes: "Moving workload between hypervisors",
    fromHv: fromHv || "Source",
    toHv: toHv || "Destination",
    showMigrationVisual: true,
    stroke: SCC_PROGRESS_STROKE_MIGRATION,
    percent: 0
  })
}

function sccUpdateMigrationOverlay(data) {
  const safePercent = Math.max(0, Math.min(100, parseInt(data.percent || 0, 10)))

  setSccOverlayTexts({
    title: `Migrating ${data.vm_name || "VM"}`,
    text: data.message || "Migrating VM...",
    bytes: `${data.from_hv || "Source"} -> ${data.to_hv || "Destination"}`,
    fromHv: data.from_hv || "Source",
    toHv: data.to_hv || "Destination"
  })
  sccSetCloneProgress(safePercent)

  if (data.status === "completed") {
    completeSccMigrationOverlay(data.message)
  } else if (data.status === "failed") {
    markCloneProgressFailed(data.error || data.message || "Migration failed.")
  }
}

function sccBeginMigrationPolling(statusUrl) {
  const poll = () => {
    sccRequestJson(statusUrl)
      .then((data) => {
        sccUpdateMigrationOverlay(data)
      })
      .catch(() => {
        markCloneProgressFailed("Failed to read migration progress.")
      })
  }

  poll()
  cloneProgressTimer = window.setInterval(poll, 1000)
}

function sccStartStatusPollingFromResponse(data, options = {}) {
  if (!data || !data.status_url) {
    ;(options.onMissingStatusUrl || markCloneProgressFailed)(options.missingStatusUrlMessage || "Progress URL was not returned.")
    return false
  }

  ;(options.beginPolling || sccBeginClonePolling)(data.status_url)
  return true
}

function sccFormatCloneBytes(bytes) {
  if (!bytes || bytes <= 0) {
    return ""
  }

  const units = ["B", "KB", "MB", "GB", "TB"]
  let index = 0
  let value = bytes

  while (value >= 1024 && index < units.length - 1) {
    value = value / 1024.0
    index += 1
  }

  return `${value.toFixed(index === 0 ? 0 : 1)} ${units[index]}`
}

function sccShowCloneOverlay(vmName) {
  showSccProgressOverlay({
    title: `Creating ${vmName}`,
    text: "Preparing VM creation...",
    bytes: "",
    showMigrationVisual: false,
    stroke: SCC_PROGRESS_STROKE_DEFAULT,
    percent: 0
  })
}

function updateCloneOverlay(data) {
  let detail = ""

  setSccOverlayTexts({
    title: data.operation_type === "disk_consolidation" ? "Consolidating VM disk" : (data.provisioning_type === "linked" ? "Creating linked clone" : "Cloning VM image"),
    text: data.message || "Processing VM creation..."
  })
  sccSetCloneProgress(data.percent || 0)

  if (data.copied_bytes && data.total_bytes) {
    detail = `${sccFormatCloneBytes(data.copied_bytes)} / ${sccFormatCloneBytes(data.total_bytes)}`
  }
  setSccOverlayTexts({ bytes: detail })

  if (data.status === "completed") {
    setSccOverlayTexts({
      text: data.message || "Clone completed."
    })
    completeSccProgressOverlay(data.redirect_url, "/vms/list", 900, data.message || "Clone completed.")
  } else if (data.status === "failed") {
    markCloneProgressFailed(data.error || data.message || "Clone failed.")
  } else {
    setSccOverlayStroke(SCC_PROGRESS_STROKE_DEFAULT)
  }
}

function sccBeginClonePolling(statusUrl) {
  const poll = () => {
    sccRequestJson(statusUrl)
      .then((data) => {
        updateCloneOverlay(data)
      })
      .catch(() => {
        markCloneProgressFailed("Failed to read clone progress.")
      })
  }

  poll()
  cloneProgressTimer = window.setInterval(poll, 1000)
}

function startInlineUploadOverlay(title, text) {
  showSccProgressOverlay({
    title,
    text,
    bytes: "",
    showMigrationVisual: false,
    stroke: SCC_PROGRESS_STROKE_DEFAULT,
    percent: 0
  })
}

function initializeStatusShell(flashMessages = []) {
  if (!initialized) {
    initialized = true
    sccReplayPendingToast()
  }

  if (flashMessages.length && !visibleToasts.value.length) {
    flashMessages.forEach((message) => {
      addToast({
        level: message.level || "info",
        text: message.text || "",
        duration: 4200
      })
    })
  }
}

onMounted(() => {
  window.sccStatusShell = {
    completeSccProgressOverlay,
    hideCloneOverlay,
    markCloneProgressFailed,
    sccBeginClonePolling,
    sccBeginMigrationPolling,
    sccDismissProgressToast,
    sccFormatCloneBytes,
    sccRequestJson,
    sccSetCloneProgress,
    sccShowCloneOverlay,
    sccShowMigrationOverlay,
    sccShowToast,
    sccStartStatusPollingFromResponse,
    setSccOverlayTexts,
    startInlineUploadOverlay,
    startSccProgress
  }
  initializeStatusShell(props.flashMessages || [])
})

onBeforeUnmount(() => {
  if (window.sccStatusShell) {
    delete window.sccStatusShell
  }
})
</script>
